<template>
  <ValidationProvider
    tag="div"
    :vid="vid"
    :name="name"
    :rules="rules"
    class="static-input-container"
    :class="styles"
    v-slot="{ errors, required }"
  >
    <div>
      <p :class="{ 'dark-mode': darkMode }">
        {{ label }}

        <span>
          {{ required ? '*' : '' }}
        </span>
      </p>
    </div>

    <div
      class="input-container"
      :class="{ icon: hasIcon(), 'dark-mode': darkMode }"
    >
      <input
        v-if="isMoney()"
        :type="inputType"
        :placeholder="placeholder"
        v-model="innerValue"
        autocomplete="off"
        :disabled="isDisabled"
        :maxlength="maxLength"
        :inputmode="inputMode"
        :class="{ error: !!errors[0], disabled: isDisabled }"
        @input="checkMoney"
      />

      <input
        v-else-if="isDate()"
        type="date"
        :placeholder="placeholder"
        v-model="innerValue"
        autocomplete="off"
        :disabled="isDisabled"
        :class="{ error: !!errors[0], disabled: isDisabled }"
      />

      <input
        v-else-if="hasMask()"
        :type="inputType"
        :placeholder="placeholder"
        v-model="innerValue"
        autocomplete="off"
        v-mask="mask"
        :disabled="isDisabled"
        :maxlength="maxLength"
        :inputmode="inputMode"
        :class="{ error: !!errors[0], disabled: isDisabled }"
      />

      <input
        v-else
        :type="inputType"
        :placeholder="placeholder"
        v-model="innerValue"
        autocomplete="off"
        :disabled="isDisabled"
        :maxlength="maxLength"
        :inputmode="inputMode"
        :class="{
          passwd: isPassword(),
          error: !!errors[0],
          disabled: isDisabled,
        }"
      />

      <div
        v-if="hasIcon() && !isDisabled"
        class="icon-container"
        @click="inputButtonClick"
      >
        <i v-if="hasButtonCleaner" class="icon-close-circle" />
        <i v-else-if="isPassword() && visibility" class="icon-eye" />
        <i v-else class="icon-eye-slash" />
      </div>

      <p>{{ errors[0] }}</p>
    </div>
  </ValidationProvider>
</template>

<script>
import { ValidationProvider } from 'vee-validate';

export default {
  name: 'InputField',

  props: {
    type: {
      type: String,
      default: 'text',
      validator(value) {
        return [
          'url',
          'text',
          'password',
          'tel',
          'search',
          'number',
          'email',
          'money',
          'date',
        ].includes(value);
      },
    },

    vid: {
      type: String,
      default: undefined,
    },

    placeholder: {
      type: String,
      default: '',
    },

    label: {
      type: String,
      default: '',
    },

    styles: {
      type: String,
      default: '',
    },

    rules: {
      type: [Object, String],
      default: '',
    },

    value: {
      type: [String, Number, Date],
      default: '',
    },

    maxLength: {
      type: Number,
      required: false,
    },

    mask: {
      type: [String, Array],
      required: false,
    },

    minCalendarDate: {
      type: [String, Date],
    },

    maxCalendarDate: {
      type: [String, Date],
    },

    refName: {
      type: String,
      required: false,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    hasButtonCleaner: {
      type: Boolean,
      default: false,
    },

    darkMode: {
      type: Boolean,
      default: false,
    },

    inputMode: {
      type: String,
      default: 'text',
      validator(value) {
        return [
          'text',
          'email',
          'tel',
          'url',
          'number',
          'decimal',
          'numeric',
          'search',
        ].includes(value);
      },
    },
  },

  components: {
    ValidationProvider,
  },

  data() {
    return {
      name: null,
      innerValue: '',
      visibility: false,
      inputType: 'text',
    };
  },

  created() {
    this.inputType = this.type;

    if (this.value) {
      this.innerValue = this.value;
    }
  },

  mounted() {
    if (this.refName) {
      this.name = this.refName;
    } else {
      this.name = this.label.toLowerCase();
    }

    if (this.isDate() && this.value) {
      // Converter DD/MM/YYYY para YYYY-MM-DD para o type="date"
      this.innerValue = this.formatToDateInput(this.value);
    } else if (this.isMoney() && this.value) {
      this.innerValue = parseInt(this.innerValue) * 100;

      this.checkMoney();
    }
  },

  watch: {
    innerValue(value) {
      if (this.isDate()) {
        // Converter YYYY-MM-DD para DD/MM/YYYY para emitir
        this.$emit('input', this.formatFromDateInput(value));
      } else if (this.isMoney()) {
        this.$emit(
          'input',
          parseInt(this.$service.removeSpecialCharacter(value)) / 100
        );
      } else {
        this.$emit('input', value);
      }
    },

    value(value) {
      if (value !== this.innerValue && !this.isMoney()) {
        if (this.isDate() && value) {
          // Converter para YYYY-MM-DD para o input type="date"
          this.innerValue = this.formatToDateInput(value);
        } else if (value) {
          this.innerValue = value;
        }
      }
    },
  },

  methods: {
    hasInnerValue() {
      return !!this.innerValue;
    },

    isPassword() {
      return this.type == 'password';
    },

    isMoney() {
      return this.type == 'money';
    },

    isDate() {
      return this.type == 'date';
    },

    toggleVisibility() {
      if (this.visibility && !this.isDisabled) {
        this.visibility = false;
        this.inputType = 'password';
      } else {
        this.visibility = true;
        this.inputType = 'text';
      }
    },

    inputButtonClick() {
      if (!this.isDisabled) {
        if (this.isPassword()) {
          this.toggleVisibility();
        } else {
          this.innerValue = '';
        }
      }
    },

    hasMask() {
      return !!this.mask;
    },

    hasIcon() {
      return (
        (this.isPassword() || this.hasButtonCleaner) && this.hasInnerValue()
      );
    },

    checkMoney() {
      var parsedValue =
        this.$service.removeSpecialCharacter(this.innerValue) / 100;

      this.innerValue = this.$service.formatBrlAmountSymbol(parsedValue);
    },

    formatToDateInput(dateString) {
      if (!dateString) return '';

      // Se for um objeto Date, converter para string ISO
      if (dateString instanceof Date) {
        return dateString.toISOString().split('T')[0];
      }

      // Se for uma string, normalizar o formato
      if (typeof dateString === 'string') {
        // Se já está em formato YYYY-MM-DD, retornar como está
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }

        // Converte DD/MM/YYYY para YYYY-MM-DD
        const parts = dateString.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      return '';
    },

    formatFromDateInput(dateString) {
      if (!dateString) return '';
      // Converte YYYY-MM-DD para DD/MM/YYYY
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateString;
    },

    resetField() {
      this.innerValue = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.static-input-container {
  width: 100%;

  p {
    margin-bottom: 0.5rem;
    color: #333;

    &.dark-mode {
      color: #f0f0f0;
    }
  }

  span {
    color: #b93a5b;
  }

  .input-container {
    &.icon {
      input {
        padding: 1rem 2.5rem 1rem 1rem;
      }
    }

    input {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      width: 100%;
      height: 54px;
      border: 1px solid transparent;
      background: #e2e8f0;
      border-radius: 6px;
      padding: 1rem;
      font-size: 14px;
      caret-color: #ff9c00;
      transition: border-color 0.2s ease;

      &:focus {
        outline: none;
        border-color: #3182ce;
        box-shadow: 0 0 0 2px rgba(49, 130, 206, 0.2);
      }

      &.money {
        cursor: pointer;
      }

      &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background: #edf2f7;
        color: #a0aec0;
      }

      &[type='number']::-webkit-inner-spin-button,
      &[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &.passwd {
        padding: 0.5rem 3rem 0.5rem 1rem;
      }

      &[type='date'] {
        cursor: pointer;

        &::placeholder {
          color: #999;
          opacity: 1;
        }
      }

      &.error {
        border: 2px solid #b93a5b;
      }
    }

    &.dark-mode {
      input {
        background: #394653;
        border: 1px solid #3a4856;
        color: #f0f0f0;
        transition: border-color 0.2s ease;

        &:focus {
          outline: none;
          border-color: #3182ce;
          box-shadow: 0 0 0 2px rgba(49, 130, 206, 0.2);
        }

        &::placeholder {
          color: #a0aab8;
        }

        &[type='date'] {
          cursor: pointer;

          &::placeholder {
            color: #a0aab8;
            opacity: 1;
          }
        }

        &.disabled {
          background: #2b3746;
          border-color: #3a4856;
          color: #8a9aaa;
        }

        &.error {
          border-color: #b93a5b;
        }
      }
    }

    .icon-container {
      color: #8e8e8e;
      float: right;
      position: relative;
      bottom: 35px;
      right: 15px;
      height: 0;
      cursor: pointer;

      i {
        font-size: 1.5rem;
        position: relative;

        &.icon-close-circle {
          font-size: 1.2rem;
          top: 1.5px;
        }
      }
    }

    p {
      color: #b93a5b;
      font-size: 12px;
      margin-top: 0.2rem;
      display: flex;
      min-width: 190px;
      position: absolute;
    }
  }
}
</style>
